import { security } from '../config/environment';
import { storageKeys } from '../config/environment';
import { resolveOAuthRedirectUri } from '../utils/oauthRedirectUri';

/**
 * Génère un code verifier aléatoire pour PKCE
 */
const generateCodeVerifier = () => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return btoa(String.fromCharCode(...array))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');
};

/**
 * Génère un code challenge à partir d'un code verifier (SHA-256)
 */
const generateCodeChallenge = async (verifier) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return btoa(String.fromCharCode(...new Uint8Array(digest)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');
};

/**
 * Génère un state aléatoire pour la protection CSRF
 */
const generateState = () => {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return btoa(String.fromCharCode(...array))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');
};

/**
 * Lance le flow OAuth en redirigeant vers le serveur d'autorisation
 */
export const initiateOAuthLogin = async () => {
    // Vérifier que les variables d'environnement sont configurées
    if (!security.client_id) {
        throw new Error('OAuth client_id non configuré');
    }
    if (!security.serverAuthorizeEndpoint) {
        throw new Error('OAuth authorize endpoint non configuré');
    }

    // Générer les paramètres PKCE
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    const state = generateState();

    // Stocker le code verifier et le state dans sessionStorage pour les récupérer après redirection
    sessionStorage.setItem('oauth_code_verifier', codeVerifier);
    sessionStorage.setItem('oauth_state', state);

    const redirectUri = resolveOAuthRedirectUri();
    const params = new URLSearchParams({
        response_type: 'code',
        client_id: security.client_id,
        redirect_uri: redirectUri,
        scope: security.scope || 'read',
        state: state,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256'
    });

    const authorizeUrl = `${security.serverAuthorizeEndpoint}?${params.toString()}`;

    if (process.env.NODE_ENV !== 'test') {
        console.info('[OAuth] Authorize', {
            client_id: security.client_id,
            redirect_uri: redirectUri,
            endpoint: security.serverAuthorizeEndpoint
        });
    }

    // Rediriger vers le serveur d'autorisation
    window.location.href = authorizeUrl;
};

/**
 * Vérifie que le state reçu correspond à celui stocké (protection CSRF)
 */
export const verifyState = (receivedState) => {
    const storedState = sessionStorage.getItem('oauth_state');
    if (!storedState || !receivedState) {
        return false;
    }
    return storedState === receivedState;
};

/**
 * Échange le code d'autorisation contre un token d'accès
 */
export const exchangeCodeForToken = async (code, redirectUri) => {
    // Vérifier que les variables d'environnement sont configurées
    if (!security.client_id || !security.client_secret) {
        throw new Error('OAuth credentials non configurées');
    }
    if (!security.serverTokenEndpoint) {
        throw new Error('OAuth token endpoint non configuré');
    }

    // Récupérer le code verifier depuis sessionStorage
    const codeVerifier = sessionStorage.getItem('oauth_code_verifier');
    if (!codeVerifier) {
        throw new Error('Code verifier manquant. Veuillez relancer la connexion.');
    }

    // Préparer les données pour l'échange
    const params = new URLSearchParams({
        grant_type: 'authorization_code',
        code: code,
        redirect_uri: redirectUri,
        client_id: security.client_id,
        code_verifier: codeVerifier
    });

    // Encoder les credentials pour l'authentification Basic
    const credentials = btoa(`${security.client_id}:${security.client_secret}`);
    const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Basic ${credentials}`
    };

    try {
        const response = await fetch(security.serverTokenEndpoint, {
            method: 'POST',
            headers: headers,
            body: params.toString()
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error_description || errorData.error || 'Erreur lors de l\'échange du code');
        }

        const data = await response.json();

        // Calculer l'expiration absolue
        const expiresAt = Date.now() + (data.expires_in * 1000);

        // Stocker la session
        const session = {
            access_token: data.access_token,
            refresh_token: data.refresh_token,
            expires_in: data.expires_in,
            expires_at: expiresAt,
            token_type: data.token_type || 'Bearer',
            scope: data.scope || security.scope,
            authenticated: true
        };

        localStorage.setItem(storageKeys.session, JSON.stringify(session));

        // Nettoyer les données PKCE de sessionStorage
        sessionStorage.removeItem('oauth_code_verifier');
        sessionStorage.removeItem('oauth_state');

        return session;
    } catch (error) {
        console.error('Erreur lors de l\'échange du code:', error);
        throw error;
    }
};
